import { useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useQuery } from '@tanstack/react-query';

import { listStudentIssuedFormsOptions } from '@drivingdoc/api-client/generated/react-query';
import { StudentIssuedForm } from 'types/students';

import useAuthContext from 'hooks/useAuthContext';

import StudentPage from '../components/StudentPage';
import FormCard from './components/FormCard/FormCard';

dayjs.extend(advancedFormat);

function StudentForms(): JSX.Element {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();
  const pageSize = 10;
  const [page, setPage] = useState(1);

  const forms = useQuery({
    ...listStudentIssuedFormsOptions({
      path: {
        id: studentId ?? '',
      },
      query: {
        page: page,
        pageSize: pageSize,
      },
    }),
    enabled: !!studentId,
  });
  const formsData = forms.data as { data: StudentIssuedForm[]; total: number } | undefined;

  const handleChangePage = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <StudentPage title="Forms">
      <Stack direction="column" spacing={2}>
        {/* render loading indicator when fetching */}
        {forms.isFetching && (
          <Box display="flex" justifyContent="center">
            <CircularProgress color="inherit" />
          </Box>
        )}

        {/* render form cards */}
        {forms.isSuccess && formsData?.data?.map((form) => <FormCard key={form.id} form={form} />)}

        {/* when there are no forms, display message */}
        {forms.isSuccess && formsData?.data?.length === 0 && (
          <Typography align="center">You do not have any forms yet.</Typography>
        )}

        {/* pagination controls */}
        <Box display="flex" justifyContent="center">
          <Pagination
            count={Math.ceil((formsData?.total ?? 0) / pageSize)}
            page={page}
            size="large"
            onChange={handleChangePage}
          />
        </Box>
      </Stack>
    </StudentPage>
  );
}

export default StudentForms;
